import type {
  Cell,
  GameResult,
  GameState,
  Index,
  MiniResult,
  Move,
  MoveResult,
  Player,
} from './types.js';

/** The eight winning lines, shared by mini-boards and the meta-board. */
export const LINES: readonly (readonly [number, number, number])[] = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8], // rows
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8], // columns
  [0, 4, 8],
  [2, 4, 6], // diagonals
];

/** Which mini-board a move lands in. */
export function boardOf(move: Move): Index {
  return Math.floor(move / 9);
}

/** Which cell within its mini-board a move lands in. */
export function cellOf(move: Move): Index {
  return move % 9;
}

/** Build a move from a mini-board index and a cell index. */
export function toMove(board: Index, cell: Index): Move {
  return board * 9 + cell;
}

export function opponent(player: Player): Player {
  return player === 'X' ? 'O' : 'X';
}

export function initialState(): GameState {
  return {
    boards: Array.from({ length: 9 }, () => Array<Cell>(9).fill(null)),
    miniResults: Array<MiniResult>(9).fill(null),
    activeBoard: null,
    turn: 'X',
    result: null,
    moves: [],
  };
}

/** Winner of a single 9-cell line set, or `null`. */
function lineWinner<T extends string>(cells: readonly (T | null)[]): T | null {
  for (const [a, b, c] of LINES) {
    const v = cells[a];
    if (v !== null && v === cells[b] && v === cells[c]) return v;
  }
  return null;
}

/** Outcome of one mini-board given its cells: winner, `'draw'` if full, else `null`. */
function miniResultOf(cells: readonly Cell[]): MiniResult {
  const winner = lineWinner(cells);
  if (winner !== null) return winner;
  return cells.every((c) => c !== null) ? 'draw' : null;
}

/**
 * Overall outcome given the per-mini-board results.
 *
 * Only outright mini-board wins count toward a meta-line; a drawn mini-board
 * belongs to neither player. The game is a draw once every mini-board is
 * resolved with no meta-line formed.
 */
function gameResultOf(miniResults: readonly MiniResult[]): GameResult {
  // Drawn mini-boards must not participate in a meta-line, so map them out.
  const claimed = miniResults.map((r) => (r === 'draw' ? null : r));
  const winner = lineWinner(claimed);
  if (winner !== null) return winner;
  return miniResults.every((r) => r !== null) ? 'draw' : null;
}

/** Why `move` cannot be played in `state`, or `null` if it is legal. */
export function illegalReason(state: GameState, move: Move) {
  if (!Number.isInteger(move) || move < 0 || move > 80) return 'out-of-range' as const;
  if (state.result !== null) return 'game-over' as const;

  const board = boardOf(move);
  const cell = cellOf(move);

  if (state.activeBoard !== null && board !== state.activeBoard) return 'wrong-board' as const;
  if (state.miniResults[board] !== null) return 'board-closed' as const;
  if (state.boards[board][cell] !== null) return 'cell-occupied' as const;
  return null;
}

export function isLegal(state: GameState, move: Move): boolean {
  return illegalReason(state, move) === null;
}

/** Every move playable from `state`, ascending. Empty once the game is over. */
export function legalMoves(state: GameState): Move[] {
  if (state.result !== null) return [];

  const moves: Move[] = [];
  const boards =
    state.activeBoard !== null
      ? [state.activeBoard]
      : Array.from({ length: 9 }, (_, i) => i);

  for (const board of boards) {
    if (state.miniResults[board] !== null) continue;
    for (let cell = 0; cell < 9; cell++) {
      if (state.boards[board][cell] === null) moves.push(toMove(board, cell));
    }
  }
  return moves;
}

/**
 * Play `move`, returning a new state. Never mutates `state`, never throws.
 *
 * The returned state is a full structural copy of the mini-board that changed;
 * every other mini-board array is shared, which is safe because all state is
 * treated as immutable.
 */
export function applyMove(state: GameState, move: Move): MoveResult {
  const reason = illegalReason(state, move);
  if (reason !== null) return { ok: false, reason };

  const board = boardOf(move);
  const cell = cellOf(move);

  // Copy only the mini-board being written to.
  const nextCells = state.boards[board].slice();
  nextCells[cell] = state.turn;
  const boards = state.boards.slice();
  boards[board] = nextCells;

  const miniResults = state.miniResults.slice();
  miniResults[board] = miniResultOf(nextCells);

  const result = gameResultOf(miniResults);

  // The cell played dictates the next board; if that board is resolved, the
  // opponent may play anywhere.
  const target = cell;
  const activeBoard = miniResults[target] === null ? target : null;

  return {
    ok: true,
    state: {
      boards,
      miniResults,
      activeBoard: result === null ? activeBoard : null,
      turn: opponent(state.turn),
      result,
      moves: [...state.moves, move],
    },
  };
}

/**
 * Rebuild a state by replaying moves from the start.
 *
 * This is the server's validation primitive: replay the stored move list plus
 * the incoming move, and a rejection means the client sent something illegal.
 */
export function replay(moves: readonly Move[]): MoveResult {
  let state = initialState();
  for (const move of moves) {
    const next = applyMove(state, move);
    if (!next.ok) return next;
    state = next.state;
  }
  return { ok: true, state };
}
